import { FieldErrors, useFormContext } from 'react-hook-form'
import { BefehlFormAuftrag, BefehlFormField } from '../Generic'
import { useTranslation } from 'react-i18next'
import { useEffect, useMemo } from 'react'
import Auftrag95_50ZeileForm from './Auftrag95_50ZeileForm'
import { RadioChipGroup, RadioChipOption } from '../../form'
import AuftragCheckbox from '../Generic/AuftragCheckbox'
import { Befehl95FormData } from './schema'
import {
  DEFAULT_AUFTRAG_95_50,
  DEFAULT_AUFTRAG_95_53,
  DEFAULT_AUFTRAG_95_54
} from './schema/befehl95-form.schema'

export interface Auftrag95_50FormProps {
  readOnly: boolean
  'data-testid': string
  activateEmptyFieldWarnings: boolean
  errors?: FieldErrors
}

export default function Auftrag95_50Form({
  readOnly,
  'data-testid': testId,
  activateEmptyFieldWarnings,
  errors
}: Readonly<Auftrag95_50FormProps>) {
  const { control, formState, watch, setValue, resetField } =
    useFormContext<Befehl95FormData>()
  const { t } = useTranslation('ui-befehle')

  const fieldErrors = useMemo(() => {
    return errors ?? formState.errors
  }, [errors, formState.errors])

  const selectionType = watch('auftrag_95_50.type')
  const auftragActive = watch('auftrag_95_50.enabled')

  const auftrag_95_53_checkbox = watch('auftrag_95_53.enabled')
  const auftrag_95_54_checkbox = watch('auftrag_95_54.enabled')

  useEffect(() => {
    if (selectionType === 'km') {
      setValue('auftrag_95_50.in_am', 'in')
    } else if (selectionType === 'signal_zugmeldestelle') {
      setValue('auftrag_95_50.in_am', 'am')
    }
  }, [selectionType, setValue])

  useEffect(() => {
    if (auftrag_95_53_checkbox) {
      setValue('auftrag_95_54.enabled', false)
    }
  }, [auftrag_95_53_checkbox, setValue])

  useEffect(() => {
    if (auftrag_95_54_checkbox) {
      setValue('auftrag_95_53.enabled', false)
    }
  }, [auftrag_95_54_checkbox, setValue])

  return (
    <BefehlFormAuftrag
      auftragKennung="95.50"
      readOnly={readOnly}
      testId={testId}
      displayKennung={`95.50`}
      fieldName={'auftrag_95_50.enabled'}
      onChange={(checked) => {
        if (!checked) {
          resetField('auftrag_95_50', {
            defaultValue: DEFAULT_AUFTRAG_95_50
          })
          resetField('auftrag_95_53', {
            defaultValue: DEFAULT_AUFTRAG_95_53
          })
          resetField('auftrag_95_54', {
            defaultValue: DEFAULT_AUFTRAG_95_54
          })
        }
      }}
    >
      <div className="@container">
        <div
          className="flex flex-col @[640px]:flex-row flex-wrap gap-1 items-baseline"
          data-testid={`${testId}-container`}
        >
          <div className="flex items-baseline gap-0.5">
            <span data-testid={`${testId}-text`}>
              {t('auftraege.95_50.title')}
            </span>
            <BefehlFormField
              name="auftrag_95_50.in_am"
              control={control}
              label=""
              readOnly={false}
              disabled={!auftragActive}
              activateEmptyWarning={false}
              data-testid={`${testId}-text-in-am`}
              render={({ field, isDisabled }) => {
                const options: RadioChipOption[] = [
                  {
                    value: 'in',
                    label: t('in')
                  },
                  {
                    value: 'am',
                    label: t('am')
                  }
                ]
                return (
                  <RadioChipGroup
                    name={field.name}
                    value={field.value}
                    onChange={field.onChange}
                    options={options}
                    disabled={isDisabled}
                    showOnlySelected={readOnly}
                    readOnly={true}
                    variant="plain"
                    testId={`${testId}-text-in-am-inner`}
                  />
                )
              }}
            />
          </div>
          <div className="w-full flex flex-col gap-1 flex-1">
            <Auftrag95_50ZeileForm
              auftragKennung={'95.50'}
              fieldPrefix={'auftrag_95_50'}
              readOnly={readOnly}
              testId={`${testId}-zeile`}
              disabled={!auftragActive}
              activateEmptyFieldWarnings={activateEmptyFieldWarnings}
              errors={fieldErrors}
            ></Auftrag95_50ZeileForm>
            <div className="flex flex-wrap gap-2">
              {(auftrag_95_53_checkbox || !readOnly) && (
                <BefehlFormField
                  name="auftrag_95_53.enabled"
                  control={control}
                  label=""
                  readOnly={readOnly}
                  disabled={!auftragActive}
                  activateEmptyWarning={false}
                  data-testid={`${testId}-auftrag-95-53`}
                  render={({ field }) => (
                    <div
                      className={
                        auftragActive ? '' : 'text-gray-500 opacity-60'
                      }
                    >
                      <div className="flex items-baseline gap-0.5">
                        <div className="flex justify-center items-start pt-0.25">
                          <AuftragCheckbox
                            checked={field.value}
                            label={`95.53`}
                            disabled={!auftragActive}
                            onChange={(e) => field.onChange(e.target.checked)}
                            readOnly={readOnly}
                            data-testid={`${testId}-auftrag-95-53-inner`}
                            errors={fieldErrors}
                          />
                        </div>
                        <span data-testid={`${testId}-auftrag-95-53-text`}>
                          {t('auftraege.95_53')}
                        </span>
                      </div>
                    </div>
                  )}
                />
              )}

              {(auftrag_95_54_checkbox || !readOnly) && (
                <BefehlFormField
                  name="auftrag_95_54.enabled"
                  control={control}
                  label=""
                  readOnly={readOnly}
                  disabled={!auftragActive}
                  activateEmptyWarning={false}
                  data-testid={`${testId}-auftrag-95-54`}
                  render={({ field }) => (
                    <div
                      className={
                        auftragActive ? '' : 'text-gray-500 opacity-60'
                      }
                    >
                      <div className="flex items-baseline gap-0.5">
                        <div className="flex justify-center items-start pt-0.25">
                          <AuftragCheckbox
                            checked={field.value}
                            label={`95.54`}
                            disabled={!auftragActive}
                            onChange={(e) => field.onChange(e.target.checked)}
                            readOnly={readOnly}
                            data-testid={`${testId}-auftrag-95-54-inner`}
                            errors={fieldErrors}
                          />
                        </div>
                        <span data-testid={`${testId}-auftrag-95-54-text`}>
                          {t('auftraege.95_54')}
                        </span>
                      </div>
                    </div>
                  )}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </BefehlFormAuftrag>
  )
}
